import {
  AppModal,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import type { PhaseActionWidget } from "@/lib/types";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PHASE_WIDGET_CATALOG } from "@/lib/data/static/phase-widget-catalog";
import { ArrowDown, ArrowUp, GripVertical, Trash2, X } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { ClaimTypeScopeSelect } from "./ClaimTypeScopeSelect";
import { PhaseWidgetPreview } from "./PhaseWidgetPreview";

const moveItem = <T,>(items: T[], index: number, direction: -1 | 1) => {
  const next = [...items];
  const destination = index + direction;
  if (destination < 0 || destination >= items.length) return items;
  [next[index], next[destination]] = [next[destination], next[index]];
  return next;
};

function SortableWidgetCard({
  widget,
  actions,
  readOnly,
  onEdit,
  renderGroupActionControls,
  showSampleData,
}: {
  widget: PhaseActionWidget;
  actions: { id: string; title: string }[];
  readOnly: boolean;
  onEdit: () => void;
  renderGroupActionControls?: (actionId: string) => ReactNode;
  showSampleData: boolean;
}) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id, disabled: readOnly });
  const wide = PHASE_WIDGET_CATALOG.find(
    (item) => item.type === widget.type,
  )?.wide;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`${wide ? "@min-[760px]:col-span-full" : ""} ${isDragging ? "relative z-10 opacity-60" : ""}`}
    >
      <PhaseWidgetPreview
        widget={widget}
        actions={actions}
        onEdit={readOnly ? undefined : onEdit}
        groupActionControls={renderGroupActionControls}
        showSampleData={showSampleData}
        headerLeading={
          readOnly ? undefined : (
            <Button
              ref={setActivatorNodeRef}
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 cursor-grab active:cursor-grabbing"
              aria-label={`Drag to reorder ${widget.title || "widget"}`}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="size-4" aria-hidden="true" />
            </Button>
          )
        }
      />
    </div>
  );
}

export function PhaseActionWidgetEditor({
  widgets,
  onChange,
  actions,
  claimTypes,
  readOnly,
  stacked,
  showSeparator,
  editingWidgetId,
  onEditingWidgetIdChange,
  renderGroupActionControls,
  showSampleData,
  newActionGroupId,
}: {
  widgets: PhaseActionWidget[];
  onChange: (widgets: PhaseActionWidget[]) => void;
  actions: { id: string; title: string }[];
  claimTypes: { id: string; label: string }[];
  readOnly: boolean;
  stacked: boolean;
  showSeparator: boolean;
  editingWidgetId?: string;
  onEditingWidgetIdChange: (id?: string, saved?: boolean) => void;
  renderGroupActionControls?: (actionId: string) => ReactNode;
  showSampleData: boolean;
  newActionGroupId?: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const reorderWidgets = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    const from = widgets.findIndex((widget) => widget.id === event.active.id);
    const to = widgets.findIndex((widget) => widget.id === event.over?.id);
    if (from >= 0 && to >= 0) onChange(arrayMove(widgets, from, to));
  };
  const groupedIds = new Set(
    widgets.flatMap((widget) => widget.actionDefinitionIds),
  );
  const availableActions = actions.filter(
    (action) => !groupedIds.has(action.id),
  );
  const updateWidget = (
    id: string,
    change: {
      title?: string;
      actionDefinitionIds?: string[];
      claimTypeIds?: string[];
    },
  ) => {
    onChange(
      widgets.map((widget) =>
        widget.id === id ? { ...widget, ...change } : widget,
      ),
    );
  };

  return (
    <div
      className={`@container space-y-3 ${showSeparator ? "border-border/70 border-t pt-4" : ""}`}
    >
      <Text variant="body" className="text-sm">
        Phase widgets
      </Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={reorderWidgets}
      >
        <SortableContext
          items={widgets.map((widget) => widget.id)}
          strategy={rectSortingStrategy}
        >
          <div className="grid gap-4 @min-[760px]:grid-cols-2">
            {widgets.map((widget, index) => (
              <Fragment key={widget.id}>
                <SortableWidgetCard
                  widget={widget}
                  actions={actions}
                  readOnly={readOnly}
                  renderGroupActionControls={renderGroupActionControls}
                  showSampleData={showSampleData}
                  onEdit={() => {
                    onEditingWidgetIdChange(widget.id);
                  }}
                />
                {editingWidgetId === widget.id ? (
                  <AppModal
                    open
                    onOpenChange={(open) => {
                      if (!open) onEditingWidgetIdChange(undefined);
                    }}
                    title={
                      widget.type === "ActionGroup"
                        ? widget.id === newActionGroupId
                          ? "Create action group"
                          : "Edit action group"
                        : `Edit ${widget.title || "widget"}`
                    }
                    description={
                      widget.type === "ActionGroup"
                        ? "Give this group a title and choose the actions it contains."
                        : "Change this widget's settings."
                    }
                    size={widget.type === "ActionGroup" ? "xl" : "3xl"}
                    preview={
                      widget.type === "ActionGroup" ? undefined : (
                        <PhaseWidgetPreview
                          widget={widget}
                          actions={actions}
                          showSampleData={showSampleData}
                        />
                      )
                    }
                    previewLabel={null}
                    previewColumnClassName="lg:w-1/2 lg:max-w-none"
                    overlayClassName={stacked ? "z-[108]" : undefined}
                    className={stacked ? "z-[109]" : undefined}
                    footer={
                      <div className="flex w-full justify-end">
                        <Button
                          type="button"
                          disabled={
                            !widget.title.trim() ||
                            (widget.type === "ActionGroup" &&
                              widget.actionDefinitionIds.length === 0)
                          }
                          onClick={() => {
                            onEditingWidgetIdChange(undefined, true);
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    }
                  >
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="w-full space-y-1.5">
                          <Label htmlFor={`widget-title-${widget.id}`}>
                            {widget.type === "ActionGroup"
                              ? "Action group title"
                              : "Widget title"}
                          </Label>
                          <Input
                            id={`widget-title-${widget.id}`}
                            value={widget.title}
                            maxLength={200}
                            disabled={readOnly}
                            placeholder={
                              widget.type === "ActionGroup"
                                ? undefined
                                : "Liability & Coverage"
                            }
                            onChange={(event) => {
                              updateWidget(widget.id, {
                                title: event.target.value,
                              });
                            }}
                          />
                        </div>
                        <ClaimTypeScopeSelect
                          className="min-w-40 flex-1"
                          disabled={readOnly}
                          allLabel="All phase claim types"
                          claimTypes={claimTypes}
                          selectedIds={widget.claimTypeIds ?? []}
                          onChange={(claimTypeIds) => {
                            updateWidget(widget.id, { claimTypeIds });
                          }}
                          label={`Claim types for ${widget.type === "ActionGroup" ? "action group" : "widget"} ${widget.title || index + 1}`}
                        />
                        {!readOnly && widget.id !== newActionGroupId ? (
                          <div className="flex shrink-0 gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={
                                widget.type === "ActionGroup"
                                  ? `Remove action group ${widget.title || index + 1} and ungroup its actions`
                                  : `Remove widget ${widget.title || index + 1}`
                              }
                              onClick={() => {
                                onChange(
                                  widgets.filter(
                                    (item) => item.id !== widget.id,
                                  ),
                                );
                                onEditingWidgetIdChange(undefined);
                              }}
                            >
                              <Trash2 className="text-destructive h-4 w-4" />
                            </Button>
                          </div>
                        ) : null}
                      </div>
                      {widget.type === "Countdown" ? (
                        <div className="space-y-2">
                          <Label htmlFor={`widget-days-${widget.id}`}>
                            Countdown duration (days)
                          </Label>
                          <Input
                            id={`widget-days-${widget.id}`}
                            type="number"
                            min={1}
                            max={3650}
                            step={1}
                            value={
                              Number.isNaN(widget.durationDays)
                                ? ""
                                : widget.durationDays
                            }
                            disabled={readOnly}
                            onChange={(event) => {
                              onChange(
                                widgets.map((item) =>
                                  item.id === widget.id &&
                                  item.type === "Countdown"
                                    ? {
                                        ...item,
                                        durationDays:
                                          event.target.valueAsNumber,
                                      }
                                    : item,
                                ),
                              );
                            }}
                          />
                          <Text variant="caption">
                            Starts on this phase&apos;s first progress and does
                            not restart when reopened. A phase with no actions
                            has no progress-based start date.
                          </Text>
                        </div>
                      ) : null}
                      {widget.type !== "ActionGroup" ? (
                        <Text variant="caption">
                          Informational widget · displays live matter data
                          without changing phase completion.
                        </Text>
                      ) : null}
                      <div className="divide-border/70 divide-y">
                        {widget.actionDefinitionIds.map((id, actionIndex) => {
                          const title =
                            actions.find((action) => action.id === id)?.title ??
                            "Unavailable Action";
                          return (
                            <div
                              key={id}
                              className="flex items-center justify-between gap-2 py-2"
                            >
                              <Text variant="body" className="text-sm">
                                {title}
                              </Text>
                              {!readOnly ? (
                                <div className="flex shrink-0 gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    disabled={actionIndex === 0}
                                    aria-label={`Move ${title} up in ${widget.type === "ActionGroup" ? "action group" : "widget"}`}
                                    onClick={() => {
                                      updateWidget(widget.id, {
                                        actionDefinitionIds: moveItem(
                                          widget.actionDefinitionIds,
                                          actionIndex,
                                          -1,
                                        ),
                                      });
                                    }}
                                  >
                                    <ArrowUp className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    disabled={
                                      actionIndex ===
                                      widget.actionDefinitionIds.length - 1
                                    }
                                    aria-label={`Move ${title} down in ${widget.type === "ActionGroup" ? "action group" : "widget"}`}
                                    onClick={() => {
                                      updateWidget(widget.id, {
                                        actionDefinitionIds: moveItem(
                                          widget.actionDefinitionIds,
                                          actionIndex,
                                          1,
                                        ),
                                      });
                                    }}
                                  >
                                    <ArrowDown className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Ungroup ${title}`}
                                    onClick={() => {
                                      updateWidget(widget.id, {
                                        actionDefinitionIds:
                                          widget.actionDefinitionIds.filter(
                                            (actionId) => actionId !== id,
                                          ),
                                      });
                                    }}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                      {widget.type === "ActionGroup" &&
                      widget.actionDefinitionIds.length === 0 ? (
                        <Text variant="caption" className="text-amber-800">
                          Add at least one action to save this group.
                        </Text>
                      ) : null}
                      {!readOnly && widget.type === "ActionGroup" ? (
                        <div className="flex items-center gap-2">
                          <Select
                            value=""
                            onValueChange={(actionId) => {
                              updateWidget(widget.id, {
                                actionDefinitionIds: [
                                  ...widget.actionDefinitionIds,
                                  actionId,
                                ],
                              });
                            }}
                            disabled={availableActions.length === 0}
                          >
                            <SelectTrigger
                              aria-label={`Add action to ${widget.title || "action group"}`}
                            >
                              <SelectValue placeholder="Select action" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableActions.map((action) => (
                                <SelectItem key={action.id} value={action.id}>
                                  {action.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                    </div>
                  </AppModal>
                ) : null}
              </Fragment>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
